import { Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { IVisitSlotsForDay } from '@app/booking-part/models/visits-slots-for-day.model';
import { IVisitSlotSearchRequest, IVisitSlot, ITreatmentAssignmentApiModel } from '@app/shared/models';
import { ISeoTagType } from '@app/shared/services/seo/seo-tag-type.enum';
import { SEOService } from '@app/shared/services/seo/seo.service';
import { VisitSlotsService } from '@app/shared/services/visit-slots/visit-slots.service';
import { Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { each } from 'lodash-es';
import { addDays, addWeeks, format, getDate, isToday, subDays, subWeeks } from 'date-fns';
import { BookingStepsFormType } from '@app/shared/models/booking-steps-form-type.model';
import { ControlContainer, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgbAccordionModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '@app/shared/shared.module';
import { BookingPartChooseSpecialistDumbComponent } from '../choose-specialist/choose-specialist.dumb.component';
import { WeekSlotScenarioEnum } from '@app/booking-part/models/week-slot-scenario.enum';

@Component({
  selector: 'app-choose-visit-slot',
  templateUrl: './choose-visit-slot.smart.component.html',
  styleUrls: ['./choose-visit-slot.smart.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgbAccordionModule,
    BookingPartChooseSpecialistDumbComponent,
    ReactiveFormsModule,
    SharedModule,
    NgbPaginationModule],
  viewProviders: [{
    provide: ControlContainer,
    useExisting: FormGroupDirective
  }]
})
export class BookingPartChooseVisitSlotSmartComponent implements OnInit, OnDestroy {
  @Output() moveToTheNextStep = new EventEmitter<boolean>();

  public availableVisitSlots: IVisitSlotsForDay[] = [];
  public allLoadedSlots: IVisitSlotsForDay[] = [];
  public hasAnySlots: boolean;
  public hasAnySlotsForThisWeek: boolean;
  public parentForm: BookingStepsFormType;
  public readonly parentFormDirective = inject(FormGroupDirective);
  public weeksCount: number;
  public slotsForOneDay: IVisitSlot[];
  public disabledArrowButton = false;
  public firstItemWithSlots: number;
  public hasNoSlotsForConsecutive30Days: boolean;

  private readonly dateFormat = 'yyyy-MM-dd';
  private readonly weekDaysCount = 7;
  private masterSubscription = new Subscription();
  private visitSlotsStartDateRange: Date;
  private visitSlotsEndDateRange: Date;
  private readonly visitSlotsService = inject(VisitSlotsService);
  private readonly seoService = inject(SEOService);
  private readonly anyEmployeeGuid = '00000000-0000-0000-0000-000000000000';

  constructor() {
    this.seoService.setSEO([
      {
        tagType: ISeoTagType.Title,
        i18nKey: 'BOOKING-PART.STEPS.VISIT-SLOTS.META-TITLE'
      },
      {
        tagType: ISeoTagType.Description,
        i18nKey: 'BOOKING-PART.STEPS.VISIT-SLOTS.META-DESCRIPTION'
      }
    ]);
  }

  ngOnInit(): void {
    this.parentForm = this.parentFormDirective.form;
    let isFirstEmission = true;

    if (this.parentForm.controls.treatmentAssingments.value.length > 0) {
      this.masterSubscription.add(
        this.loadNextWeeks(this.loadedWeeksTotalCount, WeekSlotScenarioEnum.PreviouslyChoosen)
          .subscribe());
    }

    this.masterSubscription.add(this.parentForm.controls.treatmentAssingments.valueChanges.
      pipe(
        switchMap(() => {
          if (isFirstEmission && this.parentForm.controls.treatmentAssingments.value.length > 0) {
            isFirstEmission = false;
            return this.initializeFirstWeek();
          }
          return this.reloadSlotsForEmployeeChange();
        }
        )
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  public visitSlotSelected(selectedVisitSlot: IVisitSlot): void {
    this.parentForm.controls.visitSlot.patchValue(selectedVisitSlot);
    this.moveToTheNextStep.emit(true);
  }

  public loadNextWeek(): void {
    this.disabledArrowButton = false;
    this.masterSubscription.add(this.loadNextWeeks(1, WeekSlotScenarioEnum.Next)
      .subscribe());
  }

  public searchForFirstAvailableTimeSlots(): void {
    this.fetchSlotsWithRetry().subscribe();
  }

  public loadPreviousWeek(): void {
    this.masterSubscription.add(this.loadNextWeeks(1, WeekSlotScenarioEnum.Previous)
      .subscribe());
  }

  public getWeekDayKey(current: number): string {
    return this.availableVisitSlots.map(x => x.date)[current - 1];
  }

  public getDay(current: number): number {
    return this.availableVisitSlots.map(x => getDate(new Date(x.date)))[current - 1];
  }

  public getMonth(current: number): string {
    return this.availableVisitSlots.map(x => x.date)[current - 1];
  }

  public setActiveItem(index: number): void {
    this.slotsForOneDay = this.availableVisitSlots[index - 1].slots;
  }

  public changeSpecialist(): void {
    this.parentForm.controls.treatmentAssingments.controls.map(x => x.controls.isCollapsed.setValue(false));
  }

  public get loadedWeeksTotalCount(): number {
    return this.parentForm.controls.loadedWeeksTotalCount.value;
  }

  public onPageChange(dayNumber: number): void {
    this.parentForm.controls.daySlotItem.setValue(dayNumber);
  }

  private initializeFirstWeek(): Observable<IVisitSlot[]> {
    return this.loadNextWeeks(1, WeekSlotScenarioEnum.Next)
      .pipe(
        tap(() => {
          const firstItemWithSlots = this.availableVisitSlots.findIndex(x => x.slots && x.slots.length > 0);
          this.slotsForOneDay = this.availableVisitSlots[firstItemWithSlots]?.slots;
        })
      );
  }

  private loadNextWeeks(weeksCount: number, scenario: WeekSlotScenarioEnum): Observable<IVisitSlot[]> {
    switch (scenario) {
      case WeekSlotScenarioEnum.Previous:
        this.visitSlotsStartDateRange = subWeeks(this.visitSlotsStartDateRange, weeksCount);
        this.visitSlotsEndDateRange = subWeeks(this.visitSlotsEndDateRange, weeksCount);
        break;
      case WeekSlotScenarioEnum.Next:
        if (this.loadedWeeksTotalCount === 0) {
          this.visitSlotsStartDateRange = new Date();
          this.visitSlotsEndDateRange = addDays(new Date(), this.weekDaysCount - 1);
        }
        else if (this.loadedWeeksTotalCount !== 0) {
          this.visitSlotsStartDateRange = addDays(addWeeks(this.visitSlotsEndDateRange, weeksCount - 1), 1);
          this.visitSlotsEndDateRange = addWeeks(this.visitSlotsEndDateRange, weeksCount);
        }
        break;
      case WeekSlotScenarioEnum.SearchForFirstPossibleTimeSlotIn4Weeks:
        this.visitSlotsStartDateRange = addDays(addWeeks(this.visitSlotsEndDateRange, 0), 1);
        this.visitSlotsEndDateRange = addWeeks(this.visitSlotsEndDateRange, weeksCount);
        break;
      case WeekSlotScenarioEnum.PreviouslyChoosen:
        //startDate based on previously choosen slot and slotItem position
        isToday(this.parentForm.controls.visitSlot.value.startDate.toString())
          ? this.visitSlotsStartDateRange = new Date(this.parentForm.controls.visitSlot.value.startDate.toString())
          : this.visitSlotsStartDateRange = new Date(subDays(this.parentForm.controls.visitSlot.value.startDate.toString(), this.parentForm.controls.daySlotItem.value - 1));
        this.visitSlotsEndDateRange = new Date(addDays(this.visitSlotsStartDateRange, 6));
    }
    return this.getVisitSlots(this.visitSlotsStartDateRange, this.visitSlotsEndDateRange)
      .pipe(
        tap((visitSlots: IVisitSlot[]) => {
          this.hasAnySlotsForThisWeek = visitSlots.some(y => y.startDate);
          const groupedAndFilledDays = this.groupByDayAndFillEmptyDays(visitSlots, weeksCount, new Date(this.visitSlotsStartDateRange));
          this.availableVisitSlots = groupedAndFilledDays;
          this.disabledArrowButton = isToday(new Date(this.availableVisitSlots[0]?.date));
          let currentLoadedWeeksTotalCount: number;
          switch (scenario) {
            case WeekSlotScenarioEnum.Previous:
              this.findSlotForOneDay(this.availableVisitSlots);
              this.parentForm.controls.loadedWeeksTotalCount.setValue(this.loadedWeeksTotalCount - weeksCount);
              this.allLoadedSlots.splice(-7);
              this.consecutive4WeeksWithoutVisitSlots(this.allLoadedSlots);
              this.slotsForOneDay = this.availableVisitSlots[this.parentForm.controls.daySlotItem.value - 1]?.slots;
              break;
            case WeekSlotScenarioEnum.Next:
              this.findSlotForOneDay(this.availableVisitSlots);
              currentLoadedWeeksTotalCount = this.loadedWeeksTotalCount;
              this.parentForm.controls.loadedWeeksTotalCount.setValue(currentLoadedWeeksTotalCount += weeksCount);
              this.allLoadedSlots = this.allLoadedSlots.concat(groupedAndFilledDays);
              this.consecutive4WeeksWithoutVisitSlots(this.allLoadedSlots);
              this.hasAnySlots = this.allLoadedSlots.some(y => y.slots.length > 0);
              break;
            case WeekSlotScenarioEnum.PreviouslyChoosen:
              this.allLoadedSlots = this.allLoadedSlots.concat(groupedAndFilledDays);
              this.hasAnySlots = this.allLoadedSlots.some(y => y.slots.length > 0);
              this.slotsForOneDay = this.availableVisitSlots[this.parentForm.controls.daySlotItem.value - 1]?.slots;
              break;
            case WeekSlotScenarioEnum.SearchForFirstPossibleTimeSlotIn4Weeks:
              this.findSlotForOneDay(this.availableVisitSlots);
              currentLoadedWeeksTotalCount = this.loadedWeeksTotalCount;
              this.parentForm.controls.loadedWeeksTotalCount.setValue(currentLoadedWeeksTotalCount += weeksCount);
              this.allLoadedSlots = this.allLoadedSlots.concat(groupedAndFilledDays);
              this.consecutive4WeeksWithoutVisitSlots(this.allLoadedSlots);
              this.hasAnySlots = this.allLoadedSlots.some(y => y.slots.length > 0);
              break;
          }
        })
      );
  }

  private reloadSlotsForEmployeeChange(): Observable<IVisitSlot[]> {
    this.parentForm.controls.loadedWeeksTotalCount.setValue(0);
    return this.loadNextWeeks(1, WeekSlotScenarioEnum.Next)
      .pipe(
        tap((slots) => {
          this.parentForm.controls.loadedWeeksTotalCount.setValue(1);
          this.allLoadedSlots.splice(0, this.allLoadedSlots.length);
          const groupedAndFilledDays = this.groupByDayAndFillEmptyDays(slots, 1, new Date(this.visitSlotsStartDateRange));
          this.allLoadedSlots = groupedAndFilledDays;
        }
        )
      );
  }

  private getVisitSlots(startDate: Date, endDate: Date): Observable<IVisitSlot[]> {
    const searchRequest: IVisitSlotSearchRequest = {
      startDate,
      endDate,
      treatmentAssignments: this.mapTreatmentAssingmentsForIdsOnly(),
      isNewCustomer: this.parentForm.controls.isNewCustomer.value
    };
    return this.visitSlotsService.getVisitSlots(this.parentForm.controls.salonId.value, searchRequest);
  }

  private mapTreatmentAssingmentsForIdsOnly(): ITreatmentAssignmentApiModel[] {
    return this.parentForm.controls.treatmentAssingments.controls.map((treatmentAssingment) => ({
      treatmentId: treatmentAssingment.controls.treatment.value.id,
      employeeId: treatmentAssingment.controls?.employee.value ? treatmentAssingment.controls.employee.value.id : this.anyEmployeeGuid
    }));
  }

  private groupByDayAndFillEmptyDays(visitSlots: IVisitSlot[], weeksCount: number, startDate: Date): IVisitSlotsForDay[] {
    const startDay = startDate;
    const dates: { [date: string]: IVisitSlotsForDay } = {};

    const daysToFill = (this.weekDaysCount * weeksCount);
    for (let i = 0; i < daysToFill; i++) {
      const dayToAdd = addDays(startDay, i);
      const formattedDayToAdd = format(dayToAdd, this.dateFormat);
      dates[formattedDayToAdd] = { date: formattedDayToAdd, slots: [] };
    }

    each(visitSlots, (slot: IVisitSlot) =>
      dates[format(new Date(slot.startDate), this.dateFormat)]?.slots.push(slot));
    return Object.values(dates);
  };

  private findSlotForOneDay(availableVisitSlots: IVisitSlotsForDay[]): void {
    this.firstItemWithSlots = availableVisitSlots.findIndex(x => x.slots && x.slots.length > 0);
    if (this.firstItemWithSlots === -1) {
      this.parentForm.controls.daySlotItem.setValue(1);
    }
    this.parentForm.controls.daySlotItem.setValue(this.firstItemWithSlots + 1);
    availableVisitSlots.map(x => {
      if (x.slots.length) {
        this.slotsForOneDay = availableVisitSlots[this.firstItemWithSlots].slots
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
      }
    });
  };

  private consecutive4WeeksWithoutVisitSlots(slots: IVisitSlotsForDay[]): void {
    let consecutiveDaysCount = 0;

    for (let i = 0; i < slots.length; i++) {
      if (!slots[i].slots.length) {
        consecutiveDaysCount++;
      } else {
        consecutiveDaysCount = 0;
      }
    }
    if (consecutiveDaysCount >= 28) {
      this.hasNoSlotsForConsecutive30Days = true;
    }
    else {
      consecutiveDaysCount = 0;
      this.hasNoSlotsForConsecutive30Days = false;
    }
  }

  private fetchSlotsWithRetry(): Observable<any[]> {
    const weekNumber = 1;

    return new Observable<any[]>(observer => {
      const maxWeeks = 4;
      const fetchNextWeek = () => {
        this.loadNextWeeks(weekNumber, WeekSlotScenarioEnum.SearchForFirstPossibleTimeSlotIn4Weeks)
          .pipe(
            switchMap(data => {
              if (data.length > 0 || this.parentForm.controls.loadedWeeksTotalCount.value === maxWeeks) {
                observer.next(data);
                observer.complete();
                return of(data);
              } else {
                observer.next(null);
                observer.complete();
                return of(null);
              }
            })
          )
          .subscribe({
            next: res => {
              if (res === null) {
                fetchNextWeek();
              }
            },
          });
      };
      fetchNextWeek();
    });
  }
}
